// SVG geometry definitions for the "shape" element type.
// Each entry renders inside a 100×100 viewBox so the host element scales
// the figure to its own width/height. Polygons store point pairs to keep
// the codebase agnostic about which SVG primitive is used.

import React from "react";

export type ShapeKind =
  // 2D
  | "rectangle"
  | "square"
  | "circle"
  | "ellipse"
  | "triangle"
  | "right-triangle"
  | "pentagon"
  | "hexagon"
  | "octagon"
  | "star"
  | "rhombus"
  | "trapezoid"
  | "parallelogram"
  | "arrow"
  | "line"
  | "heart"
  | "crescent"
  // 3D-look
  | "cube"
  | "cylinder"
  | "cone"
  | "sphere"
  | "pyramid"
  // Free polygon with user-editable vertices (uses element.data.points).
  | "polygon";

export interface ShapeDef {
  kind: ShapeKind;
  label: string;
  /** Primary outline as SVG path `d` or polygon `points`. */
  render: (opts: {
    fill: string;
    stroke: string;
    strokeWidth: number;
    dasharray?: string;
  }) => React.ReactNode;
}

const VIEWBOX = 100;

// Convenience to make a polygon string from x/y array.
function poly(points: Array<[number, number]>): string {
  return points.map(([x, y]) => `${x},${y}`).join(" ");
}

const make = (
  kind: ShapeKind,
  label: string,
  body: (
    fill: string,
    stroke: string,
    sw: number,
    dasharray?: string
  ) => React.ReactNode
): ShapeDef => ({
  kind,
  label,
  // Stroke width is kept constant in screen pixels via a CSS rule
  // (`vector-effect: non-scaling-stroke`) defined in QuestionEditor.css —
  // applied to every SVG primitive inside .qe-canvas__element--shape.
  render: ({ fill, stroke, strokeWidth, dasharray }) =>
    body(fill, stroke, strokeWidth, dasharray),
});

export const SHAPE_DEFS: ShapeDef[] = [
  // ─── 2D ───────────────────────────────────────────────────────
  make("rectangle", "Dikdörtgen", (fill, stroke, sw, ds) => (
    // Dikdörtgen tüm viewBox'ı doldurur — element kutusu = görünür şekil.
    // (Eskiden 20 birimlik üst/alt iç boşluk vardı; bu yüzden seçim
    // çerçevesi şeklin tepesine değil, element kutusunun tepesine
    // oturuyordu ve "üstte boşluk" oluşuyordu.)
    <rect
      x={sw / 2}
      y={sw / 2}
      width={VIEWBOX - sw}
      height={VIEWBOX - sw}
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
    />
  )),
  make("square", "Kare", (fill, stroke, sw, ds) => (
    <rect
      x={sw / 2}
      y={sw / 2}
      width={VIEWBOX - sw}
      height={VIEWBOX - sw}
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
    />
  )),
  make("circle", "Daire", (fill, stroke, sw, ds) => (
    <circle
      cx={50}
      cy={50}
      r={(VIEWBOX - sw) / 2}
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
    />
  )),
  make("ellipse", "Elips", (fill, stroke, sw, ds) => (
    <ellipse
      cx={50}
      cy={50}
      rx={(VIEWBOX - sw) / 2}
      ry={(VIEWBOX - sw) / 3}
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
    />
  )),
  make("triangle", "Üçgen", (fill, stroke, sw, ds) => (
    <polygon
      points={poly([
        [50, 5],
        [95, 95],
        [5, 95],
      ])}
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
      strokeLinejoin="round"
    />
  )),
  make("right-triangle", "Dik Üçgen", (fill, stroke, sw, ds) => (
    <polygon
      points={poly([
        [5, 5],
        [5, 95],
        [95, 95],
      ])}
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
      strokeLinejoin="round"
    />
  )),
  make("pentagon", "Beşgen", (fill, stroke, sw, ds) => (
    <polygon
      points={poly([
        [50, 5],
        [95, 38],
        [78, 95],
        [22, 95],
        [5, 38],
      ])}
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
      strokeLinejoin="round"
    />
  )),
  make("hexagon", "Altıgen", (fill, stroke, sw, ds) => (
    <polygon
      points={poly([
        [25, 5],
        [75, 5],
        [95, 50],
        [75, 95],
        [25, 95],
        [5, 50],
      ])}
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
      strokeLinejoin="round"
    />
  )),
  make("octagon", "Sekizgen", (fill, stroke, sw, ds) => (
    <polygon
      points={poly([
        [30, 5],
        [70, 5],
        [95, 30],
        [95, 70],
        [70, 95],
        [30, 95],
        [5, 70],
        [5, 30],
      ])}
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
      strokeLinejoin="round"
    />
  )),
  make("star", "Yıldız", (fill, stroke, sw, ds) => (
    <polygon
      points={poly([
        [50, 5],
        [61, 38],
        [95, 38],
        [68, 58],
        [78, 92],
        [50, 72],
        [22, 92],
        [32, 58],
        [5, 38],
        [39, 38],
      ])}
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
      strokeLinejoin="round"
    />
  )),
  make("rhombus", "Eşkenar Dörtgen", (fill, stroke, sw, ds) => (
    <polygon
      points={poly([
        [50, 5],
        [95, 50],
        [50, 95],
        [5, 50],
      ])}
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
      strokeLinejoin="round"
    />
  )),
  make("trapezoid", "Yamuk", (fill, stroke, sw, ds) => (
    <polygon
      points={poly([
        [25, 15],
        [75, 15],
        [95, 85],
        [5, 85],
      ])}
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
      strokeLinejoin="round"
    />
  )),
  make("parallelogram", "Paralelkenar", (fill, stroke, sw, ds) => (
    <polygon
      points={poly([
        [25, 15],
        [95, 15],
        [75, 85],
        [5, 85],
      ])}
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
      strokeLinejoin="round"
    />
  )),
  make("arrow", "Ok", (fill, stroke, sw, ds) => (
    <polygon
      points={poly([
        [5, 40],
        [60, 40],
        [60, 20],
        [95, 50],
        [60, 80],
        [60, 60],
        [5, 60],
      ])}
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
      strokeLinejoin="round"
    />
  )),
  make("line", "Çizgi", (_fill, stroke, sw, ds) => (
    <line
      x1={5}
      y1={50}
      x2={95}
      y2={50}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
      strokeLinecap="round"
    />
  )),
  make("heart", "Kalp", (fill, stroke, sw, ds) => (
    <path
      d="M50,86 C18,62 8,42 22,28 C32,18 45,22 50,32 C55,22 68,18 78,28 C92,42 82,62 50,86 Z"
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
      strokeLinejoin="round"
    />
  )),
  // Hilal — sağa açılan klasik ay biçimi: büyük bir dairenin içinden
  // daha küçük bir oval çıkarılarak iki yay olarak path'lenir.
  make("crescent", "Hilal", (fill, stroke, sw, ds) => (
    <path
      d="M60 10 A 42 42 0 1 0 60 90 A 30 36 0 1 1 60 10 Z"
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
      strokeLinejoin="round"
    />
  )),
  // ─── 3D-look ──────────────────────────────────────────────────
  make("cube", "Küp", (fill, stroke, sw, ds) => (
    <g
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
      strokeLinejoin="round"
    >
      {/* front face */}
      <polygon points="5,35 65,35 65,95 5,95" />
      {/* top */}
      <polygon
        points="5,35 35,5 95,5 65,35"
        fill={shade(fill, 0.85)}
      />
      {/* right */}
      <polygon
        points="65,35 95,5 95,65 65,95"
        fill={shade(fill, 0.7)}
      />
    </g>
  )),
  make("cylinder", "Silindir", (fill, stroke, sw, ds) => (
    <g
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
    >
      <path
        d="M10,18 a40,10 0 1,0 80,0 L90,82 a40,10 0 1,1 -80,0 Z"
        fill={fill}
      />
      <ellipse cx="50" cy="18" rx="40" ry="10" fill={shade(fill, 0.9)} />
    </g>
  )),
  make("cone", "Koni", (fill, stroke, sw, ds) => (
    <g
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
      strokeLinejoin="round"
    >
      <path d="M50,5 L90,82 a40,10 0 1,1 -80,0 Z" fill={fill} />
      <ellipse cx="50" cy="82" rx="40" ry="10" fill={shade(fill, 0.85)} />
    </g>
  )),
  make("sphere", "Küre", (fill, stroke, sw, ds) => (
    <g>
      <defs>
        <radialGradient id="qe-sphere-grad" cx="35%" cy="30%" r="65%">
          <stop offset="0%" stopColor={shade(fill, 1.4)} />
          <stop offset="100%" stopColor={shade(fill, 0.65)} />
        </radialGradient>
      </defs>
      <circle
        cx={50}
        cy={50}
        r={(VIEWBOX - sw) / 2}
        fill="url(#qe-sphere-grad)"
        stroke={stroke}
        strokeWidth={sw}
        strokeDasharray={ds}
      />
    </g>
  )),
  make("pyramid", "Piramit", (fill, stroke, sw, ds) => (
    <g
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={ds}
      strokeLinejoin="round"
    >
      {/* base */}
      <polygon points="10,85 50,95 90,85 50,75" fill={shade(fill, 0.65)} />
      {/* left face */}
      <polygon points="10,85 50,10 50,95" fill={shade(fill, 0.85)} />
      {/* right face */}
      <polygon points="90,85 50,10 50,95" fill={fill} />
    </g>
  )),
];

export const SHAPE_BY_KIND: Record<ShapeKind, ShapeDef> = Object.fromEntries(
  SHAPE_DEFS.map((s) => [s.kind, s])
) as Record<ShapeKind, ShapeDef>;

/**
 * Default polygon vertex set for a given shape kind, in the same 0–100
 * viewBox coordinates used everywhere else. Used when the user opts to
 * "explode to vertices" — converts the parametric shape into a free polygon
 * the user can edit point-by-point.
 *
 * Shapes that have curves (circle, ellipse, heart) or 3D constructions
 * (cube, cylinder…) intentionally return null — converting them to a
 * polygon would discard their character.
 */
export const POLYGON_POINTS_FOR_SHAPE: Partial<
  Record<ShapeKind, Array<{ x: number; y: number }>>
> = {
  rectangle: [
    { x: 5, y: 5 },
    { x: 95, y: 5 },
    { x: 95, y: 95 },
    { x: 5, y: 95 },
  ],
  square: [
    { x: 5, y: 5 },
    { x: 95, y: 5 },
    { x: 95, y: 95 },
    { x: 5, y: 95 },
  ],
  triangle: [
    { x: 50, y: 5 },
    { x: 95, y: 95 },
    { x: 5, y: 95 },
  ],
  "right-triangle": [
    { x: 5, y: 5 },
    { x: 5, y: 95 },
    { x: 95, y: 95 },
  ],
  pentagon: [
    { x: 50, y: 5 },
    { x: 95, y: 38 },
    { x: 78, y: 95 },
    { x: 22, y: 95 },
    { x: 5, y: 38 },
  ],
  hexagon: [
    { x: 25, y: 5 },
    { x: 75, y: 5 },
    { x: 95, y: 50 },
    { x: 75, y: 95 },
    { x: 25, y: 95 },
    { x: 5, y: 50 },
  ],
  octagon: [
    { x: 30, y: 5 },
    { x: 70, y: 5 },
    { x: 95, y: 30 },
    { x: 95, y: 70 },
    { x: 70, y: 95 },
    { x: 30, y: 95 },
    { x: 5, y: 70 },
    { x: 5, y: 30 },
  ],
  star: [
    { x: 50, y: 5 },
    { x: 61, y: 38 },
    { x: 95, y: 38 },
    { x: 68, y: 58 },
    { x: 78, y: 92 },
    { x: 50, y: 72 },
    { x: 22, y: 92 },
    { x: 32, y: 58 },
    { x: 5, y: 38 },
    { x: 39, y: 38 },
  ],
  rhombus: [
    { x: 50, y: 5 },
    { x: 95, y: 50 },
    { x: 50, y: 95 },
    { x: 5, y: 50 },
  ],
  trapezoid: [
    { x: 25, y: 15 },
    { x: 75, y: 15 },
    { x: 95, y: 85 },
    { x: 5, y: 85 },
  ],
  parallelogram: [
    { x: 25, y: 15 },
    { x: 95, y: 15 },
    { x: 75, y: 85 },
    { x: 5, y: 85 },
  ],
  arrow: [
    { x: 5, y: 40 },
    { x: 60, y: 40 },
    { x: 60, y: 20 },
    { x: 95, y: 50 },
    { x: 60, y: 80 },
    { x: 60, y: 60 },
    { x: 5, y: 60 },
  ],
  line: [
    { x: 5, y: 50 },
    { x: 95, y: 50 },
  ],
};

/** True if a preset shape can be exploded into editable polygon vertices. */
export function canExplodeToPolygon(kind: ShapeKind): boolean {
  return kind in POLYGON_POINTS_FOR_SHAPE;
}

/**
 * Shapes whose corners can be rounded with a numeric "cornerRadius" data
 * field. Excludes shapes that are already curved (circle, ellipse, heart,
 * sphere) or that are built from multiple polygons (3D solids), where
 * rounding wouldn't make visual sense.
 */
const ROUNDABLE_POLYGON_KINDS: Set<ShapeKind> = new Set<ShapeKind>([
  "rectangle",
  "square",
  "triangle",
  "right-triangle",
  "pentagon",
  "hexagon",
  "octagon",
  "star",
  "rhombus",
  "trapezoid",
  "parallelogram",
  "arrow",
  "polygon",
]);

export function isRoundableShape(kind: ShapeKind): boolean {
  return ROUNDABLE_POLYGON_KINDS.has(kind);
}

/**
 * Generate an SVG path `d` for a closed polygon with rounded corners.
 * - `r=0` → identical to drawing the polygon outright.
 * - Each corner is replaced with a quadratic Bézier whose control point is
 *   the original vertex; the curve enters/leaves the corner `r` units before
 *   the vertex along each adjacent edge. `r` is automatically clamped per
 *   corner to half of the shorter neighboring edge so it never overshoots.
 */
export function roundedPolygonPath(
  points: Array<{ x: number; y: number }>,
  r: number
): string {
  const n = points.length;
  if (n < 3) return "";
  if (r <= 0) {
    return (
      "M " +
      points.map((p) => `${p.x},${p.y}`).join(" L ") +
      " Z"
    );
  }
  let d = "";
  for (let i = 0; i < n; i++) {
    const prev = points[(i - 1 + n) % n];
    const curr = points[i];
    const next = points[(i + 1) % n];
    const dx1 = prev.x - curr.x;
    const dy1 = prev.y - curr.y;
    const dx2 = next.x - curr.x;
    const dy2 = next.y - curr.y;
    const len1 = Math.hypot(dx1, dy1) || 1;
    const len2 = Math.hypot(dx2, dy2) || 1;
    // Don't let the rounding consume more than half of either neighboring edge.
    const cr = Math.min(r, len1 / 2, len2 / 2);
    const ax = curr.x + (dx1 / len1) * cr;
    const ay = curr.y + (dy1 / len1) * cr;
    const bx = curr.x + (dx2 / len2) * cr;
    const by = curr.y + (dy2 / len2) * cr;
    d += i === 0 ? `M ${ax},${ay} ` : `L ${ax},${ay} `;
    d += `Q ${curr.x},${curr.y} ${bx},${by} `;
  }
  d += "Z";
  return d;
}

// ─── Helpers ──────────────────────────────────────────────────────

/** Multiply each RGB channel by `factor` (0..2). Used for 3D-look shading. */
function shade(hex: string, factor: number): string {
  const c = (hex || "#cbd5e1").replace("#", "");
  if (c.length !== 6) return hex;
  const r = clamp(Math.round(parseInt(c.slice(0, 2), 16) * factor));
  const g = clamp(Math.round(parseInt(c.slice(2, 4), 16) * factor));
  const b = clamp(Math.round(parseInt(c.slice(4, 6), 16) * factor));
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
function clamp(n: number) {
  return Math.max(0, Math.min(255, n));
}
function toHex(n: number) {
  return n.toString(16).padStart(2, "0");
}
